import React, { useEffect, useState } from 'react';
import sanitizeHtml from 'utils/sanitizeHtml';
import { FormattedMessage } from 'react-intl';
import { Tag, Modal} from 'antd';
import DefaultLayout from 'components/DefaultLayout';
import dayjs from 'dayjs';
import { EyeOutlined } from '@ant-design/icons';
import { QTable, Button } from 'ui';
import { useT } from 'hooks/Locale/useT';
import { Filter } from 'components/Modals';
import {connect} from 'react-redux';
import {
    ownerFilter,
    dateFilter,
    customerFilter,
    organizationFilter,
    customerMailFilter,
    organizationMailFilter,
    templateFilters,
} from './filter';
import './styles.scss';
import useQueryApiClient from 'utils/useApiClient';
import { config } from 'config/config';

type FilterBy = 'CUSTOMER' | 'ORGANIZATION' | undefined;

type Filter = {
    filterBy?: FilterBy;
    customer?: string | number;
    organization?: string | number;
};

type Module = { endpoint?: string; [k: string]: unknown };

type SessionState = {
    active_user_type?: string;
    customer_id?: string | number;
};

type Props = {
    session: SessionState;
};

type MailHistoryRow = {
    id: number | string;
    type?: number;
    from?: string | null;
    fullName?: string | null;
    email?: string | null;
    phoneNumber?: string | null;
    sentToEmail?: string | null;
    sentToPhoneNumber?: string | null;
    to?: { phone?: string | null; email?: string | null } | string | null;
    executionTime?: string;
    body?: string;
    errors?: string;
    status?: string;
};

const mapStateToProps = (state: any) => {
    return {
        session: state.session
    }
};

const getReceiverAddress = (record: MailHistoryRow): string => {
    const isEmail = record.type === 0;
    if (isEmail) {
        return (
            record.sentToEmail ??
            record.email ??
            (typeof record.to === 'object' ? record.to?.email : null) ??
            '-'
        );
    }

    return (
        record.sentToPhoneNumber ??
        record.phoneNumber ??
        (typeof record.to === 'object' ? record.to?.phone : null) ??
        '-'
    );
};

const EmailHistoryList: React.FC<Props> = ({session}) => {
    const [reload, setReload] = useState(false);
    const [filter, setFilter] = useState<Record<string, any>>({});
    const [selectedRowKeys, setSelectedRowKeys] = useState<any>([]);

    const [isModalVisible, setISModalVisible] = useState(false);
    const [modalContent, setModalContent] = useState('');
    const [modalTitle, setModalTitle] = useState('');
    const t = useT();

    const columns = [
        {
            title: t('emailing.templates.template_category'),
            dataIndex: 'category',
            key: 'category',
            render: (category: string) => (
                <Tag>
                    <FormattedMessage id={`email_templates.categories.${category}`} />
                </Tag>
            ),
        },
        {
            title: t('emailing.templates.template_name'),
            dataIndex: 'title',
            key: 'title',
        },
        {
            title: t('emailing.history.receiver_name'),
            dataIndex: 'fullName',
        },
        {
            title: t('emailing.history.senders_address'),
            dataIndex: 'from',
            render: (text: string, record: MailHistoryRow) => {
                return (
                    <div className="history-sender">
                        {record.from}
                        <>
                            {record.type === 1 ? (
                                <Tag>
                                    <FormattedMessage id="users.export.table.smsSending" />
                                </Tag>
                            ) : null}
                        </>
                    </div>
                );
            },
        },
        {
            title: t('emailing.history.receiver_address'),
            dataIndex: 'to',
            render: (text: string, record: MailHistoryRow) => getReceiverAddress(record),
        },
        {
            title: t('general.date'),
            dataIndex: 'executionTime',
            sorter: true,
            key: 'date',
            render: (text: string, record: any) =>
                dayjs(record.executionTime).format(config.defaultFullDateFormat),
        },
        {
            title: t('general.body'),
            dataIndex: 'body',
            key: 'body',
            render: (text: string, record: MailHistoryRow) => {
                return (
                    <p
                        className="history-link"
                        onClick={() => {
                            setModalTitle(t('emailing.history.email_body'));
                            setModalContent(record.body ?? '');
                            setISModalVisible(true);
                        }}
                    >
                        <FormattedMessage id={'general.show'} /> <EyeOutlined />
                    </p>
                );
            },
        },
        {
            title: t('general.status'),
            dataIndex: 'status',
            render: (text: string, record: MailHistoryRow, tag: any) => {
                let color;
                if (record.status) {
                    if (record.status === 'SENT') color = 'green';
                    if (record.status === 'SENDING') color = 'gold';
                    if (record.status === 'FAILED') color = 'red';
                    if (record.status === 'SCHEDULED') color = 'blue';
                    return (
                        <>
                            <Tag color={color} key={tag}>
                                <FormattedMessage id={`email_history.status.${record.status.toLowerCase()}`} />
                            </Tag>
                            {record.status === 'FAILED' ? (
                                <p
                                    className="history-link"
                                    onClick={() => {
                                        setModalTitle(t('general.error'));
                                        setModalContent(record.errors ?? '');
                                        setISModalVisible(true);
                                    }}
                                >
                                    <FormattedMessage id={'general.show'} /> <EyeOutlined />
                                </p>
                            ) : null}
                        </>
                    );
                } else {
                    return '-';
                }
            },
        },
    ];

    const { appendData, isLoading: resendIsLoading } = useQueryApiClient({
        request: {
            disableOnMount: true,
            url: '/api/v1/mail-history/resend',
            method: 'POST',
        },
        onSuccess: () => {
            setReload(true);
            setSelectedRowKeys([]);
        },
    });

    const isCustAdmin = session.active_user_type === 'CUSTOMER_ADMIN';

    const effectiveFilterBy: 'CUSTOMER' | 'ORGANIZATION' =
    isCustAdmin ? 'CUSTOMER' : (filter?.filterBy === 'CUSTOMER' ? 'CUSTOMER' : 'ORGANIZATION');

    const isCustomer = effectiveFilterBy === 'CUSTOMER';

    const customerId = isCustomer ? (filter?.customer ?? session.customer_id) : undefined;
    const organizationId = !isCustomer ? filter?.organization : undefined;
    const f: Filter = (typeof filter === 'object' && filter) || {};

    const withQuery = (endpoint: string, q: Record<string, unknown>) => {
        const qs = new URLSearchParams(
            Object.entries(q).filter(([, v]) => v !== undefined && v !== null) as [string, string][]
        ).toString();
        return qs ? `${endpoint}${endpoint.includes('?') ? '&' : '?'}${qs}` : endpoint;
    };

    useEffect(() => {
        if (isCustAdmin && filter?.filterBy !== 'CUSTOMER') {
            setFilter(f => ({ ...f, filterBy: 'CUSTOMER' }));
        }
    }, [isCustAdmin])

    const modules = [
        ...(isCustAdmin ? [] : ownerFilter),

        ...((isCustomer ? customerFilter : organizationFilter).map(m => ({
            ...m,
            endpoint: withQuery(m.endpoint, {
            filterBy: effectiveFilterBy,
            customerId: isCustomer ? customerId : undefined,
            organizationId: !isCustomer ? organizationId : undefined,
            _sig: `${isCustomer ? 'C' : 'O'}|${customerId ?? ''}|${organizationId ?? ''}`,
            }),
        }))),

        ...dateFilter,

        ...(isCustomer && customerId
            ? customerMailFilter.map(mf => ({
                ...mf,
                endpoint: mf.endpoint.replace('{id}', String(customerId)),
            }))
            : []),

        ...(!isCustomer && organizationId
            ? organizationMailFilter.map(mf => ({
                ...mf,
                endpoint: mf.endpoint.replace('{id}', String(organizationId)),
            }))
            : []),

        ...templateFilters,
    ];


    return (
        <DefaultLayout.PageLayout>
            <DefaultLayout.PageHeader
                title={t('emailing.history.e_mailing_history')}
            />
            <DefaultLayout.PageContent>
                <Filter
                    setFilter={setFilter}
                    resetFields={{
                        filterBy: ['customer', 'organization', 'templateId'],
                        organization: ['customer', 'templateId'],
                        customer: ['organization', 'templateId'],
                    }}
                    modules={modules}
                    submit={() => setReload(true)}
                    hasSearch={true}
                    searchKey='qu'
                />
                <QTable
                    tableKey="programHistory"
                    columns={columns}
                    url="/api/v1/mail-history"
                    setReload={setReload}
                    rowSelection={true}
                    reload={reload}
                    selectedRowKeys={selectedRowKeys}
                    setSelectedRowKeys={setSelectedRowKeys}
                    rowKey="id"
                    filter={filter}
                />
            </DefaultLayout.PageContent>
            <DefaultLayout.PageFooter
                left={
                    <>
                        <Button
                            loading={resendIsLoading}
                            onClick={() => appendData({ historyId: selectedRowKeys })}
                            message="general.resend"
                        />
                    </>
                }
            />
            <Modal
                title={modalTitle}
                open={isModalVisible}
                onOk={() => setISModalVisible(false)}
                onCancel={() => setISModalVisible(false)}
                cancelButtonProps={{ style: { display: 'none' } }}
            >
                <div dangerouslySetInnerHTML={{ __html: sanitizeHtml(modalContent) }} />
            </Modal>
        </DefaultLayout.PageLayout>
    );
};

export default connect(mapStateToProps)(EmailHistoryList);
